// TournamentsListPage — public list of tournaments. Admin sees "New
// tournament" button + every tournament; non-admin sees the same list but
// without the Manage entry point.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trophy, Plus, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { tournamentAPI, TOURNAMENT_STATUS_LABELS } from '../lib/tournamentApi';
import CreateTournamentDialog from '../components/tournaments/CreateTournamentDialog';

const STATUS_COLOR = {
  draft:                  'bg-[#f3f1ec] text-[#6a766e]',
  registration_open:      'bg-emerald-50 text-emerald-800',
  registration_closed:    'bg-amber-50 text-amber-800',
  bracket_generated:      'bg-sky-50 text-sky-800',
  in_progress:            'bg-indigo-50 text-indigo-800',
  completed:              'bg-[#013523] text-white',
  archived:               'bg-[#f3f1ec] text-[#6a766e]',
};

export default function TournamentsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [search, setSearch] = useState('');

  const refresh = () => {
    tournamentAPI.list().then((r) => setRows(r.data)).catch(() => setRows([]));
  };
  useEffect(() => { refresh(); }, []);

  const filtered = (rows || []).filter((t) => {
    if (!search.trim()) return true;
    return t.name.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="space-y-6" data-testid="tournaments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#013523] tracking-tight flex items-center gap-3">
            <Trophy size={36} weight="duotone" className="text-[#c9a93a]" />
            Tournaments
          </h1>
          <p className="text-sm text-[#6a766e] mt-1">
            Single-elimination brackets. Singles &amp; doubles.
          </p>
        </div>
        {isAdmin && (
          <button
            data-testid="new-tournament-btn"
            onClick={() => setOpenCreate(true)}
            className="px-3 py-2 rounded-full bg-[#013523] text-white text-sm flex items-center gap-1">
            <Plus size={14} /> New tournament
          </button>
        )}
      </div>

      <div className="relative max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6a766e]" />
        <input
          data-testid="tournaments-search"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tournaments…"
          className="w-full pl-9 pr-3 py-2 border border-[#e3dcd1] rounded-full text-sm" />
      </div>

      {rows === null && <div className="text-sm text-[#6a766e]" data-testid="tournaments-loading">Loading…</div>}
      {rows !== null && filtered.length === 0 && (
        <div className="text-sm text-[#6a766e] border border-[#e3dcd1] rounded-2xl p-6 text-center"
          data-testid="tournaments-empty">
          No tournaments yet.{isAdmin && ' Click "New tournament" to create one.'}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {filtered.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`tournament-card-${t.id}`}
            onClick={() => navigate(`/tournaments/${t.id}`)}
            className="text-left border border-[#e3dcd1] rounded-2xl p-4 hover:border-[#013523] transition-colors bg-white"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="font-medium text-[#013523]">{t.name}</div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_COLOR[t.status] || 'bg-[#f3f1ec] text-[#6a766e]'}`}>
                {TOURNAMENT_STATUS_LABELS[t.status] || t.status}
              </span>
            </div>
            <div className="mt-2 text-xs text-[#6a766e] flex gap-3 flex-wrap">
              <span>{t.tournament_type === 'singles' ? 'Singles' : 'Doubles'}</span>
              <span>·</span>
              <span>{t.entry_count} {t.entry_count === 1 ? 'entry' : 'entries'}</span>
              {t.bracket_size && (
                <>
                  <span>·</span>
                  <span>{t.bracket_size}-slot bracket</span>
                </>
              )}
            </div>
            {t.match_format_text && (
              <div className="mt-1 text-xs text-[#6a766e]">{t.match_format_text}</div>
            )}
          </button>
        ))}
      </div>

      <CreateTournamentDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={(t) => { refresh(); navigate(`/tournaments/${t.id}/manage`); }}
      />

      <div className="text-xs text-[#6a766e] mt-8">
        <Link to="/events" data-testid="back-to-events" className="underline">← Back to Events</Link>
      </div>
    </div>
  );
}
